import API from '@/api/apiCreate'
import { getParams } from '@/api/getParams'
import { isError } from '@/helpers/checkQuery'
import { ResponseWrapper } from '@/api/types/base.types'
import {
  CreateTaskTrackerColumnRequest,
  MoveTaskTrackerColumnsItem,
  TaskTrackerBoardColumn,
  TaskTrackerBoardLaneItem,
  UpdateTaskTrackerColumnRequest,
} from '../store/taskTracker.types'
import {
  mapBoardColumnFromApi,
  mapBoardColumnsFromApi,
  mapBoardItemsFromApi,
} from './taskTracker.api.mappers'
import { getApiErrorMessage, TASK_TRACKER_API_REQUEST_CONFIG } from './taskTracker.api.utils'
import { ApiBoardColumn, ApiBoardColumnsMoveResponse } from './taskTracker.api.types'

export const taskTrackerColumnsApi = {
  async createColumn(
    boardId: number,
    payload: CreateTaskTrackerColumnRequest,
  ): Promise<TaskTrackerBoardColumn> {
    const res = await API.post<ResponseWrapper<ApiBoardColumn>>(
      `task-tracker-columns/create${getParams({ board_id: boardId })}`,
      {
        title: payload.title.trim(),
        color: payload.color.trim(),
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось создать колонку'))
    }
    if (!res.data?.data) {
      throw new Error('Не удалось создать колонку')
    }
    return mapBoardColumnFromApi(res.data.data)
  },

  async updateColumn(
    columnId: number,
    payload: UpdateTaskTrackerColumnRequest,
  ): Promise<TaskTrackerBoardColumn> {
    const body: UpdateTaskTrackerColumnRequest = {}
    if (payload.title !== undefined) {
      body.title = payload.title.trim()
    }
    if (payload.color !== undefined) {
      body.color = payload.color.trim()
    }
    if (payload.position !== undefined) {
      body.position = payload.position
    }

    const res = await API.post<ResponseWrapper<ApiBoardColumn>>(
      `task-tracker-columns/update${getParams({ column_id: columnId })}`,
      body,
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось сохранить колонку'),
      )
    }
    if (!res.data?.data) {
      throw new Error('Не удалось сохранить колонку')
    }
    return mapBoardColumnFromApi(res.data.data)
  },

  async moveColumns(
    boardId: number,
    items: MoveTaskTrackerColumnsItem[],
  ): Promise<TaskTrackerBoardLaneItem[]> {
    const res = await API.post<ResponseWrapper<ApiBoardColumnsMoveResponse>>(
      `task-tracker-columns/move${getParams({ board_id: boardId })}`,
      {
        items: items.map((item) => ({
          id: item.id,
          type: item.type ?? 'column',
          position: item.position,
        })),
      },
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(
        getApiErrorMessage(res, 'Не удалось изменить порядок колонок'),
      )
    }

    const data = res.data?.data
    if (data?.board_items?.length) {
      return mapBoardItemsFromApi(data.board_items)
    }

    if (!data?.columns?.length) {
      throw new Error('Не удалось изменить порядок колонок')
    }

    return mapBoardItemsFromApi(
      data.columns.map((column) => ({
        ...column,
        type: 'column' as const,
      })),
    )
  },

  async deleteColumn(columnId: number): Promise<{ deleted: boolean }> {
    const res = await API.post<ResponseWrapper<{ deleted: boolean }>>(
      `task-tracker-columns/delete${getParams({ column_id: columnId })}`,
      {},
      TASK_TRACKER_API_REQUEST_CONFIG,
    )
    if (isError(res)) {
      throw new Error(getApiErrorMessage(res, 'Не удалось удалить колонку'))
    }
    return {
      deleted: Boolean(res.data?.data?.deleted),
    }
  },
}
